import React, { useState } from 'react';
import { Box, Button, TextField, Typography, ToggleButton, ToggleButtonGroup, Autocomplete } from '@mui/material';
import type { WizardState } from '../agent/types';

/**
 * 予約作成の残り項目を**1枚で**入力するフォーム（2026-10-03 UI見直し）。
 * 実運用: 予約作成10件でスタッフ入力142回のうち78回が「スキップ」、作成1件の中央値7分。
 * 空欄はスキップ扱い。必須（予約者名）が空なら確定できない。
 */
interface Props {
  wizard: WizardState;
  onSubmit: (values: Record<string, any>) => void;
  onOneByOne: () => void;
}

export default function AgentWizardForm({ wizard, onSubmit, onOneByOne }: Props) {
  const fields = wizard.fields.slice(wizard.currentIndex);
  const [values, setValues] = useState<Record<string, any>>({});
  const set = (k: string, v: any) => setValues((cur) => ({ ...cur, [k]: v }));
  const missingRequired = fields.some((f) => f.required && !String(values[f.key] ?? '').trim());

  const submit = () => {
    const out: Record<string, any> = {};
    for (const f of fields) {
      const v = values[f.key];
      if (v === undefined || v === null || v === '') continue;
      if (f.inputType === 'number') {
        const n = parseInt(String(v), 10);
        if (Number.isFinite(n)) out[f.key] = n;
      } else if (f.inputType === 'yes_no') {
        out[f.key] = v === true;
      } else {
        const t = String(v).trim();
        if (t) out[f.key] = t;
      }
    }
    onSubmit(out);
  };

  return (
    <Box sx={{ p: 1, background: '#f8fafc', borderTop: '1px solid #e2e8f0', maxHeight: '55vh', overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.8 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, flex: 1 }}>
          予約の詳細（空欄はスキップ）
        </Typography>
        <Button size="small" variant="text" color="inherit" onClick={onOneByOne} sx={{ textTransform: 'none', fontSize: 11 }}>
          1問ずつ答える
        </Button>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 0.8 }}>
        {fields.map((f) => (
          // 候補から選ぶ欄（2026-10-19）: 2〜3択はボタン（請求書をまとめるか）、それ以上は入力補完（予約経路）
          f.inputType === 'select' && (f.options?.length ?? 0) <= 3 ? (
            <Box key={f.key} sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="caption" color="text.secondary">{f.label ?? f.key}{f.required ? '（必須）' : ''}</Typography>
              <ToggleButtonGroup
                size="small" exclusive fullWidth
                value={values[f.key] ?? null}
                onChange={(_e, v) => set(f.key, v)}
              >
                {(f.options ?? []).map((o) => <ToggleButton key={o} value={o} sx={{ py: 0.3, textTransform: 'none' }}>{o}</ToggleButton>)}
              </ToggleButtonGroup>
            </Box>
          ) : f.inputType === 'select' ? (
            <Autocomplete
              key={f.key}
              freeSolo size="small"
              options={f.options ?? []}
              value={values[f.key] ?? ''}
              onInputChange={(_e, v) => set(f.key, v)}
              renderInput={(params) => (
                <TextField {...params} label={`${f.label ?? f.key}${f.required ? '（必須）' : ''}`} placeholder={f.placeholder}
                  inputProps={{ ...params.inputProps, style: { fontSize: 16 } }} />
              )}
            />
          ) : f.inputType === 'yes_no' ? (
            <Box key={f.key}>
              <Typography variant="caption" color="text.secondary">{f.label ?? f.key}</Typography>
              <ToggleButtonGroup
                size="small" exclusive fullWidth
                value={values[f.key] === undefined ? null : values[f.key]}
                onChange={(_e, v) => set(f.key, v)}
              >
                <ToggleButton value={true} sx={{ py: 0.3 }}>あり</ToggleButton>
                <ToggleButton value={false} sx={{ py: 0.3 }}>なし</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          ) : (
            <TextField
              key={f.key}
              size="small"
              label={`${f.label ?? f.key}${f.required ? '（必須）' : ''}`}
              placeholder={f.placeholder}
              type={f.inputType === 'number' ? 'number' : 'text'}
              value={values[f.key] ?? ''}
              onChange={(e) => set(f.key, e.target.value)}
              autoFocus={f.required}
              sx={f.key === 'comment' ? { gridColumn: '1 / -1' } : undefined}
              // iOS Safari は font-size 16px 未満で自動ズームするため 16px に統一
              inputProps={{ style: { fontSize: 16 } }}
            />
          )
        ))}
      </Box>
      <Button
        fullWidth variant="contained" size="small" sx={{ mt: 1, textTransform: 'none', fontWeight: 700 }}
        disabled={missingRequired}
        onClick={submit}
      >
        この内容で確認へ進む
      </Button>
    </Box>
  );
}
